import {
  CreateTalkFlowParams,
  DocumentType,
  DocumentTypeByRecordMode,
  RecordDetail,
  RecordFilter,
  RecordMode,
  ShareMode,
} from '@/types/api';
import { INIT_RECORD_STATE } from '@/recoil/init_data';
import dayjs from 'dayjs';
import { formatAs } from '@/utils/format';

export const filterToParams = (data: RecordFilter, isTemp = false) => {
  const { subject, internal, external, attachments, content, memo, relations } =
    data;

  const obj: CreateTalkFlowParams = {
    subject: subject,
    content: content,
    memo: memo,
    participant: {
      internal: [...internal.map((res) => res.passportId)],
      external: [...external],
    },
    attachments: [
      ...attachments.map((res) => ({
        name: res.name,
        uuid: res.uuid,
        size: res.size,
      })),
    ],
    relations: [
      ...relations.map((res) => ({
        name: res.name,
        link: res.link,
        type: res.type,
        id: res.id,
      })),
    ],
    config: {
      meetingDate: formatAs(data.meetingDate, 'dateTime'),
      meetingType: data.meetingType,
      meetingTypeId: data.meetingTypeId,
      isTemporary: isTemp,
      meetingMsLink: data.meetingMsLink,
      meetingMsSubject: data.meetingMsSubject,
      meetingMsId: data.meetingMsId,
      documentType: DocumentTypeByRecordMode[data.mode],
    },
  };
  return obj;
};

export const modeResponseToFilter = (data?: DocumentType) => {
  switch (data) {
    case 'new':
      return RecordMode.NEW;
    case 'note':
      return RecordMode.Note;
    case 'meeting':
    default:
      return RecordMode.Meeting;
  }
};

// 손상 파일은 loadedmetadata·error 가 둘 다 안 오기도 한다. 그때 promise 가 영영
// 안 끝나면 호출부가 업로드 중 상태로 멈추므로, 이 시간이 지나면 0 으로 끊는다.
const AUDIO_METADATA_TIMEOUT_MS = 10000;

/**
 * 오디오 blob 의 재생 길이(초)를 구한다.
 * 길이를 확정하지 못하면(디코딩 실패·타임아웃·NaN·Infinity) 0 을 반환하므로,
 * 호출부는 0 을 '빈 파일이거나 손상' 신호로 함께 취급하면 된다. (PORTAL-12592)
 */
export const getAudioDuration = (blob: Blob): Promise<number> => {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata'; // 핵심!
    audio.src = URL.createObjectURL(blob);

    let settled = false;
    const finish = (duration: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timerId);
      URL.revokeObjectURL(audio.src);
      // 손상 파일은 NaN/Infinity 가 나올 수 있어 0(=길이 확정 실패)으로 정규화
      resolve(Number.isFinite(duration) && duration > 0 ? duration : 0);
    };
    const timerId = setTimeout(() => finish(0), AUDIO_METADATA_TIMEOUT_MS);

    audio.addEventListener('loadedmetadata', () => {
      if (audio.duration === Infinity) {
        // 스트리밍 녹음(webm)은 길이를 바로 못 주므로 끝으로 seek 해서 확정시킨다
        audio.currentTime = Number.MAX_SAFE_INTEGER;
        audio.ontimeupdate = () => {
          audio.ontimeupdate = null;
          finish(audio.duration);
          audio.currentTime = 0;
        };
      } else {
        finish(audio.duration);
      }
    });

    audio.addEventListener('error', () => finish(0));
  });
};

export const responseToFilter = (data: RecordDetail): RecordFilter => {
  return {
    ...INIT_RECORD_STATE,
    docId: data.id ?? '',
    mode: modeResponseToFilter(data?.documentType),
    subject: data?.subject ?? '',
    meetingMsSubject: data.meetingMsSubject ?? '',
    meetingMsLink: data.meetingMsLink ?? '',
    meetingMsId: data.meetingMsId ?? '',
    meetingDate: data?.meetingDate ? dayjs(data?.meetingDate) : null,
    meetingType: data?.meetingType ?? '',
    meetingTypeId: '',
    internal: data?.participants.internal ?? [],
    external: data?.participants.external ?? [],
    recordings: (data?.recordings ?? []).map((r) => ({
      ...r,
      state: 'complete',
    })),
    attachments: data?.attachments ?? [],
    content: data?.content ?? '',
    memo: data?.memo ?? '',
    relations: data?.relations ?? [],
    createdBy: data.createdBy ?? null,
    createAt: data.createdAt ?? '',
    commentGroupId: data.commentGroupId ?? '',
    isBookmarked: data.isBookmarked,
  };
};

export const roleToFilter = (role: string): ShareMode => {
  switch (role) {
    case 'owner':
      return ShareMode.Owner;
    case 'sharer':
      return ShareMode.Share;
    case 'reader':
    default:
      return ShareMode.Comment;
  }
};

export const base64ToFile = (
  base64: string,
  fileName: string,
  contentType: string,
): File => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });
  return new File([blob], fileName, { type: contentType });
};

export const formatBytes = (
  bytes: number,
  decimals = 1,
  isBinary = false,
): string => {
  if (bytes === 0) return '0 Bytes';

  const k = isBinary ? 1024 : 1000;
  const units = isBinary
    ? ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB']
    : ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

  return `${size} ${units[i]}`;
};

export const EMPTY_SUBJECT = '(제목 없는 문서)';

export const getTalkFlowDisplaySubject = (
  subject?: string | null,
  suggestedSubject?: string | null,
  fallback = '',
) => {
  return subject?.trim() || suggestedSubject?.trim() || fallback;
};
